import {
  Color,
  Texture,
  RepeatWrapping,
  LinearFilter,
  LinearMipmapLinearFilter,
  Vector2,
} from "three";

import { BaseManager } from "./BaseManager.js";
import { MaterialSphere } from "../utils/material.sphere.js";
import Util from "../utils/utils.js";
import { GUI } from "three/examples/jsm/libs/lil-gui.module.min.js";
import { MaterialLoader } from "../lib/MaterialLoader.js";
import { CommonCommand } from "../command/commonCommand.js";

export class MaterialManager extends BaseManager {
  constructor(manager) {
    super(manager);
  }

  init(manager) {
    this.sceneManager = manager.sceneManager;
    this.options = manager.options;

    this.materialSphere = new MaterialSphere(manager.el); //右上角材质球

    this.matDataMap = new Map(); // 只保存修改了的材质列表
    this.allMats = new Map(); // 缓存场景中所有材质的引用
    this.currentSelectMat = null; // 当前选中的材质

    //this._createGUI();
  }

  update(dt) {
    super.update(dt);
  }

  clear() {
    this.matDataMap.clear();
    this.matDataMap = null;
    this.allMats.clear();
    this.allMats = null;
    this.materialSphere.dispose();
    this.materialSphere = null;
    super.clear();
  }

  _createGUI() {
    const effectController = {
      scale: 1,
      rotate: 0,
    };
    const gui = new GUI();

    let guiChanged = () => {
      this.setTextureScale(effectController.scale);
      this.setTextureRotation(effectController.rotate);
    };

    gui.add(effectController, "scale", 1, 10, 0.1).onChange(guiChanged);
    gui.add(effectController, "rotate", 0, 360, 1).onChange(guiChanged);

    guiChanged();
  }

  /**
   * 复制材质属性
   * @param {*} sourceMat 被复制的材质
   * @param {*} targetMat 目标材质，将被复制的材质属性复制到目标材质
   * @returns
   */
  _copyMaterialProperties(sourceMat, targetMat) {
    // 排除 Object3D 相关属性和特殊字段
    const excludeList = ["uuid", "id", "type", "dispose"];

    // 遍历源材质所有属性
    for (const key in sourceMat) {
      if (
        !excludeList.includes(key) && // 排除指定字段
        targetMat.hasOwnProperty(key) && // 目标材质支持该属性
        // 跳过函数
        typeof sourceMat[key] !== "function"
      ) {
        // // 特殊处理颜色类型属性
        // if (sourceMat[key] instanceof Color) {
        //   targetMat[key].copy(sourceMat[key]);
        // }
        // // 处理纹理属性（创建新引用）
        // else if (sourceMat[key] instanceof Texture) {
        //   targetMat[key] = sourceMat[key].clone();
        //   targetMat[key].needsUpdate = true;
        // }
        // // 普通属性直接赋值
        // else
        {
          targetMat[key] = sourceMat[key];
        }
      }
    }

    // 标记材质需要更新
    targetMat.needsUpdate = true;
    return targetMat;
  }

  /**
   * 从地址加载材质信息
   * @param {*} path
   * @returns
   */
  _loadMaterialFromJson(path, cb) {
    return new Promise((resolve, reject) => {
      const loader = new MaterialLoader();
      // load a resource
      loader.load(
        // resource URL
        path,
        // onLoad callback
        (material) => {
          if (cb) cb(material);

          resolve(material);
        },
        null,
        // onError callback
        function (err) {
          console.log("An error happened");
          reject(err);
        }
      );
    });
  }

  /**
   * 从数据库恢复模型材质数据 需要先加载完成所有的模型，包括创建的基础模型
   * @param {*} data
   */
  async setInitData(data) {
    // 缓存场景中所有材质的引用
    for (var i = 0; i < this.sceneManager.singleModels.length; i++) {
      const obj = this.sceneManager.singleModels[i];
      obj.traverse((node) => {
        if (node.material && node.material.matId) {
          if (!this.allMats.has(node.material.matId)) {
            this.allMats.set(node.material.matId, node.material);
          } else {
            // 材质关联的mesh引用数量
            if (!node.material._ref) node.material._ref = 2;
            else node.material._ref++;
          }
        }
      });
    }

    if (!data) return;
    for (var i = 0; i < data.length; i++) {
      var item = data[i];
      if (!item.version || item.version !== 2) continue;
      let targetMat = this.allMats.get(item.matId);

      if (targetMat) {
        // 从材质库加载材质
        if (item.url) {
          const mat = await this._loadMaterialFromJson(item.url);
          this._copyMaterialProperties(mat, targetMat);
        }
        if (item.color) targetMat.color = new Color(item.color);
        // 设置纹理缩放
        if (item.textureScale) {
          this._setTextureScale(targetMat, item.textureScale);
        }
        // 设置纹理方向
        if (item.textureOritation) {
          this._setTextureRotation(targetMat, item.textureOritation);
        }
        targetMat.name = item.name;

        if (!this.matDataMap.has(item.matId)) {
          this.matDataMap.set(item.matId, targetMat);
        }
      } else {
        //console.log("场景中不存在材质：" + item.matId); // 被删除的模型材质
      }
    }
  }

  /**
   * 缓存被修改的材质  内部使用
   * @param {*} mat
   */
  _saveMat() {
    if (!this.currentSelectMat) return;
    if (!this.matDataMap.has(this.currentSelectMat.matId))
      this.matDataMap.set(this.currentSelectMat.matId, this.currentSelectMat);
  }

  /**
   * 设置纹理贴图缩放 内部使用
   * @param {*} mat
   * @param {Number} scale
   */
  _setTextureScale(mat, scale) {
    if (mat && mat.map) {
      mat.map.repeat.set(scale, scale);
      mat.map.wrapS = RepeatWrapping;
      mat.map.wrapT = RepeatWrapping;
      mat.map.minFilte = LinearMipmapLinearFilter;
      mat.map.magFilter = LinearFilter;
      mat.map.needsUpdate = true;
    }
  }

  /**
   * 设置纹理贴图的旋转  内部使用
   * @param {*} mat
   * @param {Number} angle 角度，0-360
   */
  _setTextureRotation(mat, angle) {
    if (mat && mat.map) {
      mat.map.center.set(0.5, 0.5);
      mat.map.rotation = Util.degToRad(angle);
      mat.map.needsUpdate = true;
    }
  }

  /**
   * 设置当前选中材质的颜色
   * @param {*} color
   * @returns
   */
  setColor(color) {
    if (!this.currentSelectMat) return;
    this.currentSelectMat.color = new Color(color);

    this._saveMat();

    this.materialSphere.updateMaterial(this.currentSelectMat);
  }

  /**
   * 获取当前选中材质的颜色
   * @returns
   */
  getColor() {
    if (!this.currentSelectMat) return "#ffffff";
    return "#" + this.currentSelectMat.color.getHexString();
  }

  /**
   * 设置当前选中材质的纹理缩放
   * @param {*} value
   */
  setTextureScale(value) {
    if (!this.currentSelectMat) return;

    this._setTextureScale(this.currentSelectMat, value);

    this._saveMat();
  }

  /**
   * 设置贴图旋转
   * @param {*} angle 角度 0-360
   * @returns
   */
  setTextureRotation(angle) {
    if (!this.currentSelectMat) return;

    this._setTextureRotation(this.currentSelectMat, angle);

    this._saveMat();
  }

  /**
   * 获取当前选中材质的纹理缩放
   * @returns
   */
  getTextureScale() {
    if (!this.currentSelectMat) return 1;
    if (!this.currentSelectMat.map) return 1;
    let scale =
      (this.currentSelectMat.map.repeat.x +
        this.currentSelectMat.map.repeat.y) /
      2;
    return scale;
  }

  /**
   * 获取旋转角度
   * @returns
   */
  getTextureRotation() {
    if (!this.currentSelectMat) return 0;
    if (!this.currentSelectMat.map) return 0;
    let angle = Util.radToDeg(this.currentSelectMat.map.rotation);
    if (angle > 360) angle = angle % 360; // 转成0-360
    if (angle < 0) angle = angle + 360; // 转成0-360

    return angle;
  }

  /**
   * 设置当前选中的材质
   * @param {*} mat
   */
  setSelectMat(mat) {
    // 选中同一个材质就取消
    if (mat && this.currentSelectMat && this.currentSelectMat === mat) {
      this._cancleSelectForMatlib();
      return;
    }

    this._cancleSelectForMatlib();
    if (!mat) return;

    this.currentSelectMat = mat;

    this._addShaderChunck(this.currentSelectMat);

    this.materialSphere.updateMaterial(this.currentSelectMat);
  }

  _addShaderChunck(mat) {
    if (!this.argunmentChunk_matlib) {
      this.argunmentChunk_matlib = `
       #include <common>
        uniform vec2 uResolution;
        uniform float uSpacing;
        uniform float uRadius;
    `;
    }

    if (!this.fragmentChunk_matlib) {
      this.fragmentChunk_matlib = `
        #include <dithering_fragment>

        // 替换原片元着色器中的黑点生成逻辑
        vec2 uv = gl_FragCoord.xy / uResolution;

        // 1. 坐标系修正：将Y轴翻转（适配左上角原点）
        uv.y = 1.0 - uv.y;

        // 2. 宽高比补偿（保持正圆）
        float aspect = uResolution.x / uResolution.y;
        uv.x *= aspect;

        // 3. 网格映射（强制正方形单元格）
        float spacing = uSpacing;
        vec2 gridPos = uv * (1.0 / spacing);
        vec2 cell = fract(gridPos);

        // 4. 圆形计算（反向补偿宽高比）
        vec2 offset = cell - vec2(0.5);
        //offset.x /= aspect; // 关键修正点
        float dist = length(offset);

        // 5. 抗锯齿 + 颜色混合（保留原材质）
        float smoothEdge = fwidth(dist) * 2.0; // 自动边缘平滑
        float mask = smoothstep(uRadius + smoothEdge, uRadius - smoothEdge, dist);
        gl_FragColor = mix(gl_FragColor, vec4(0.0, 0.0, 0.0, 1.0), mask);
        `;
    }

    // 给材质添加选中效果（阵列黑点）
    mat.onBeforeCompile = (shader) => {
      // 添加自定义uniforms
      shader.uniforms.uResolution = {
        value: new Vector2(window.innerWidth, window.innerHeight),
      };
      shader.uniforms.uSpacing = { value: 0.04 };
      shader.uniforms.uRadius = { value: 0.07 };

      // 在着色器中插入uniform声明
      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <common>",
        this.argunmentChunk_matlib
      );

      // 替换片元着色器中的dithering部分，添加黑点逻辑
      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <dithering_fragment>",
        this.fragmentChunk_matlib
      );
    };
    mat.needsUpdate = true;
  }

  _removeShaderChunck(mat) {
    if (mat) {
      //   去掉材质的选中效果（阵列黑点）
      mat.onBeforeCompile = (shader) => {
        // 去掉自定义uniforms
        if (shader.uniforms.uResolution) delete shader.uniforms.uResolution;
        if (shader.uniforms.uSpacing) delete shader.uniforms.uSpacing;
        if (shader.uniforms.uRadius) delete shader.uniforms.uRadius;

        // 在着色器中去掉uniform声明
        shader.fragmentShader = shader.fragmentShader.replace(
          this.argunmentChunk_matlib,
          "#include <common>"
        );

        // 替换片元着色器中的dithering部分，去掉黑点逻辑
        shader.fragmentShader = shader.fragmentShader.replace(
          this.fragmentChunk_matlib,
          "#include <dithering_fragment>"
        );
      };
      mat.needsUpdate = true;
    }
  }

  /**
   * 材质库模式取消选中mesh
   */
  _cancleSelectForMatlib() {
    if (this.currentSelectMat) {
      this._removeShaderChunck(this.currentSelectMat);
    }
    this.currentSelectMat = null;
  }

  /**
   * 生成当前选中材质的截图 base64
   * @returns
   */
  getCurrentSnapshotUrl() {
    if (!this.currentSelectMat) return null;

    return this.materialSphere.getScreenshot(); //生成截图（base64）
  }

  /**
   * 场景中新增模型
   * @param {*} model
   */
  addNewModel(model) {
    if (!model) return;
    model.traverse((node) => {
      if (node.material && node.material.matId) {
        if (!this.allMats.has(node.material.matId)) {
          this.allMats.set(node.material.matId, node.material);
        } else {
          // 材质关联的mesh引用数量
          if (!node.material._ref) node.material._ref = 2;
          else node.material._ref++;
        }
      }
    });
  }

  /**
   * 删除模型
   * @param {} model
   */
  deleteNode(model) {
    if (!model) return;

    // 被删除的模型不是整体，属于图纸生成的构建之一，不能直接删除
    if (model._isPeperSingle) {
      model.visible = false;
      model._delete = true;
      model.userData._deleteSrcScale = model.scale.clone();
      model.scale.set(0, 0, 0);
      return;
    }

    // 作为其他模型的复制源时，不能直接删除
    if (model._copys && model._copys.length > 0) {
      // 把复制源相关属性给第一个复制体，将第一个复制体作为复制源
      const copyObj = model._copys[0];
      copyObj._copys = model._copys.slice(1);
      copyObj._modelUrl = model._modelUrl;
      model.userData._deleteSrcCopy = copyObj;
      delete copyObj._copySrcObj;
      delete copyObj.isCopyObj;

      for (var i = 0; i < copyObj._copys.length; i++) {
        copyObj._copys[i]._copySrcObj = copyObj;
      }

      if (this.manager.interface.copyUtil) {
        this.manager.interface.copyUtil.deleteByModelId(copyObj.modelId);
      }
    }
    // // 作为复制体
    // else if (model._copySrcObj) {
    //   if (this.manager.interface.copyUtil) {
    //     this.manager.interface.copyUtil.deleteByModelId(model.modelId);
    //   }
    // }

    model.traverse((node) => {
      if (node.material && node.material.matId) {
        if (!node.material._ref || node.material._ref <= 2) {
          //node.material.dispose();
          if (this.allMats.has(node.material.matId))
            this.allMats.delete(node.material.matId);
        } else {
          node.material._ref--;
        }
      }
      // if (node.geometry) {
      //   node.geometry.dispose();
      // }
    });
    model.userData._deleteParent = model.parent;
    model.removeFromParent();
  }

  /**
   * 还原被删除的节点，用于撤销
   * @param {*} model
   * @returns
   */
  restoreNode(model) {
    if (!model) return;
    // 被删除的模型不是整体，属于图纸生成的构建之一，不能直接删除
    if (model._isPeperSingle) {
      model.visible = true;
      delete model._delete;
      model.scale.copy(model.userData._deleteSrcScale);
      delete model.userData._deleteSrcScale;
      return;
    }

    // 作为其他模型的复制源时，不能直接删除
    if (model._copys && model._copys.length > 0) {
      const copyObj = model.userData._deleteSrcCopy;
      delete model.userData._deleteSrcCopy;
      model._copys = copyObj._copys;
      model._copys.push(copyObj);
      delete copyObj._copys;
      delete copyObj._modelUrl;
      copyObj.isCopyObj = true;
      for (var i = 0; i < model._copys.length; i++) {
        model._copys[i]._copySrcObj = model;
      }

      if (this.manager.interface.copyUtil) {
        this.manager.interface.copyUtil.copyRecord.push(copyObj._record);
      }
    }

    this.addNewModel(model); // 恢复模型的mat

    model.userData._deleteParent.add(model);
    delete model.userData._deleteParent;
  }

  /**
   * 获取结构树
   */
  getMeshTree() {
    let hierarchy = [];
    for (var i = 0; i < this.sceneManager.singleModels.length; i++) {
      const obj = this.sceneManager.singleModels[i];
      if (obj._delete) continue;
      hierarchy.push({
        name: obj.name,
        modelId: obj.modelId,
        nodeId: obj.nodeId,
        visible: obj.visible,
      });
    }
    return hierarchy;
  }

  /**
   * 将材质库的材质数据给当前选中的材质
   * @param {*} url 材质库的url
   * @returns
   */
  async replaceMatFromLib(url, matName, cb) {
    if (!this.currentSelectMat) return;

    const mat = await this._loadMaterialFromJson(url);
    mat.name = matName; // 替换材质名称
    mat._matLibUrl = url; // 替换材质库地址，用于保存材质库地址

    const srcMat = this.currentSelectMat.clone();
    srcMat.name = this.currentSelectMat.name;
    srcMat._matLibUrl = this.currentSelectMat._matLibUrl;

    // 先去掉选中效果
    this._removeShaderChunck(this.currentSelectMat);
    this._copyMaterialProperties(mat, this.currentSelectMat);
    // 替换材质属性后在加上选中效果 需要延迟一会，不然原始材质贴图无法清空？
    setTimeout(() => {
      this._addShaderChunck(this.currentSelectMat);
    }, 10);

    this.materialSphere.updateMaterial(this.currentSelectMat); //更新右上角材质球

    this.currentSelectMat._matLibUrl = url;
    this.currentSelectMat.name = matName;
    this._saveMat();

    // 历史记录
    {
      const state = {
        oldMat: srcMat,
        newMat: mat,
      };
      const cmd = new CommonCommand(
        state,
        (state) => {
          this._removeShaderChunck(this.currentSelectMat);
          this._copyMaterialProperties(state.oldMat, this.currentSelectMat);
          setTimeout(() => {
            this._addShaderChunck(this.currentSelectMat);
            this.materialSphere.updateMaterial(this.currentSelectMat); //更新右上角材质球
            this.manager.interface.updateMatlibForHistory();
          }, 10);
          if (state.oldMat._matLibUrl)
            this.currentSelectMat._matLibUrl = state.oldMat._matLibUrl;
          else delete this.currentSelectMat._matLibUrl;
          this.currentSelectMat.name = state.oldMat.name;
        },
        (state) => {
          this._removeShaderChunck(this.currentSelectMat);
          this._copyMaterialProperties(state.newMat, this.currentSelectMat);
          setTimeout(() => {
            this._addShaderChunck(this.currentSelectMat);
            this.materialSphere.updateMaterial(this.currentSelectMat); //更新右上角材质球
            this.manager.interface.updateMatlibForHistory();
          }, 10);
          if (state.newMat._matLibUrl)
            this.currentSelectMat._matLibUrl = state.newMat._matLibUrl;
          else delete this.currentSelectMat._matLibUrl;
          this.currentSelectMat.name = state.newMat.name;
        }
      );
      this.manager.addHistory(cmd);
    }

    if (cb) cb();
  }

  /**
   * 获取当前场景中所有被修改过的材质数据列表
   * @returns
   */
  save() {
    const res = [];

    this.matDataMap.forEach((value, key) => {
      const item = {
        name: value.name,
        matId: value.matId,
        color: "#" + value.color.getHexString(),
        version: 2,
      };
      if (value.map) {
        item.textureScale = (value.map.repeat.x + value.map.repeat.y) / 2;
        item.textureRotation = Util.radToDeg(value.map.rotation);
      }
      if (value._matLibUrl) {
        item.url = value._matLibUrl;
      }
      res.push(item);
    });
    return res;
  }
}
